import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import WhatsAppButton from "../../components/feature/WhatsAppButton";

const services = [
  {
    icon: "ri-hospital-line",
    key: "residential",
    title: "Medical & Clinic Cleaning",
    image: "https://readdy.ai/api/search-image?query=spotless%20modern%20medical%20clinic%20hallway%20with%20gleaming%20linoleum%20floors%20and%20sterile%20white%20walls%2C%20professional%20cleaning%20crew%20in%20uniform%2C%20bright%20fluorescent%20lighting%2C%20healthcare%20facility%20cleanliness%2C%20premium%20medical%20environment%20photography%2C%20Washington%20state%20hospital%20interior&width=600&height=400&seq=svc_clinic001&orientation=landscape",
    benefits: [
      "Hospital-grade disinfection protocols",
      "Exam rooms & waiting areas sanitized",
      "Cross-contamination prevention",
      "Restrooms & medical waste areas",
      "OSHA-compliant cleaning methods",
    ],
  },
  {
    icon: "ri-building-4-line",
    key: "commercial",
    title: "Corporate Office Cleaning",
    image: "https://readdy.ai/api/search-image?query=professional%20cleaning%20crew%20in%20navy%20uniforms%20cleaning%20a%20modern%20bright%20corporate%20office%20space%20with%20glass%20walls%2C%20white%20desks%20and%20polished%20floors%2C%20commercial%20cleaning%2C%20organized%20workspace%2C%20professional%20photography%2C%20premium%20corporate%20environment%20Seattle&width=600&height=400&seq=svc_office001&orientation=landscape",
    benefits: [
      "Daily, weekly or monthly service",
      "Workstations, lobbies & conference rooms",
      "Restrooms & break rooms sanitized",
      "Floor care & maintenance",
      "Discreet, after-hours scheduling",
    ],
  },
  {
    icon: "ri-bank-line",
    key: "deep",
    title: "Bank & Financial Institution Cleaning",
    image: "https://readdy.ai/api/search-image?query=gleaming%20professional%20bank%20interior%20with%20polished%20marble%20floors%2C%20spotless%20teller%20counters%2C%20clean%20glass%20partitions%20and%20elegant%20lighting%2C%20commercial%20banking%20facility%20cleaning%20result%2C%20premium%20financial%20institution%20environment%20photography&width=600&height=400&seq=svc_bank001&orientation=landscape",
    benefits: [
      "Teller areas & lobby floors deep-cleaned",
      "Vault corridors sanitized",
      "ATM stations & touch surfaces",
      "Security camera area compliance",
      "Scheduled off-hours service",
    ],
  },
  {
    icon: "ri-car-line",
    key: "moveinout",
    title: "Car Dealership Cleaning",
    image: "https://readdy.ai/api/search-image?query=pristine%20luxury%20car%20dealership%20showroom%20with%20gleaming%20epoxy%20floors%20and%20spotless%20glass%20walls%2C%20polished%20luxury%20cars%20on%20display%2C%20bright%20LED%20lighting%2C%20professional%20commercial%20cleaning%20result%2C%20modern%20automotive%20showroom%20interior%20photography&width=600&height=400&seq=svc_dealer001&orientation=landscape",
    benefits: [
      "Customer lounge & waiting areas",
      "Service bay & garage cleaning",
      "Exterior windows & glass (reachable height only)",
      "Consistent presentation standards",
    ],
  },
  {
    icon: "ri-hotel-bed-line",
    key: "airbnb",
    title: "Airbnb & Short-Term Rental",
    image: "https://readdy.ai/api/search-image?query=perfectly%20staged%20modern%20luxury%20airbnb%20apartment%20with%20pristine%20white%20linens%2C%20spotless%20kitchen%20and%20living%20area%2C%20professional%20short%20term%20rental%20cleaning%2C%20premium%20interior%2C%20warm%20elegant%20lighting%2C%20staged%20for%20photography&width=600&height=400&seq=svc_airbnb001&orientation=landscape",
    benefits: [
      "Quick turnovers between guests",
      "Hotel-quality bed making",
      "Restocking toiletries & towels",
      "Checklist photo documentation",
      "Superhost rating support",
    ],
  },
  {
    icon: "ri-store-line",
    key: "office",
    title: "Retail & Commercial Space Cleaning",
    image: "https://readdy.ai/api/search-image?query=spotless%20modern%20retail%20store%20or%20commercial%20space%20interior%20with%20gleaming%20floors%2C%20clean%20display%20counters%2C%20polished%20glass%20shelving%2C%20professional%20commercial%20cleaning%20result%2C%20bright%20lighting%2C%20premium%20retail%20environment%20photography&width=600&height=400&seq=svc_retail001&orientation=landscape",
    benefits: [
      "Sales floors & display areas",
      "Fitting rooms & stockrooms",
      "Customer restrooms sanitized",
      "Entrance & window cleaning",
      "Flexible scheduling around store hours",
    ],
  },
];

const ServicesPage = () => {
  const { t } = useTranslation();
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const cards = entry.target.querySelectorAll(".svc-card");
            cards.forEach((card, i) => {
              setTimeout(() => {
                card.classList.add("opacity-100", "translate-y-0");
                card.classList.remove("opacity-0", "translate-y-10");
              }, i * 120);
            });
          }
        });
      },
      { threshold: 0.05 }
    );
    if (heroRef.current) observer.observe(heroRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div style={{ fontFamily: "'Poppins', sans-serif" }}>
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-[#0F2A44] overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://readdy.ai/api/search-image?query=modern%20commercial%20building%20interior%20architectural%20detail%2C%20clean%20geometric%20patterns%2C%20polished%20surfaces%2C%20professional%20business%20environment%2C%20deep%20navy%20tones%2C%20premium%20corporate%20backdrop&width=1920&height=600&seq=svcbg_commercial001&orientation=landscape"
            alt=""
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <span className="text-[#D4AF37] text-xs font-bold uppercase tracking-widest mb-4 block">
            {t("services_label")}
          </span>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-5">
            {t("services_headline")}
          </h1>
          <p className="text-white/70 text-lg max-w-2xl mx-auto">
            {t("services_sub")}
          </p>
          <div className="flex justify-center mt-8 text-sm text-white/50">
            <Link to="/" className="hover:text-[#D4AF37] transition-colors duration-200">
              Home
            </Link>
            <span className="mx-2">/</span>
            <span className="text-[#D4AF37]">Services</span>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section id="services-list" className="py-20 bg-white" ref={heroRef}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            {services.map((svc) => (
              <div
                key={svc.key}
                className="svc-card opacity-0 translate-y-10 transition-all duration-500 rounded-2xl overflow-hidden border border-gray-100 hover:border-[#D4AF37]/30 group"
                style={{ boxShadow: "0 2px 20px rgba(0,0,0,0.06)" }}
              >
                {/* Image */}
                <div className="relative overflow-hidden h-52">
                  <img
                    src={svc.image}
                    alt={svc.title}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0F2A44]/60 to-transparent" />
                  <div className="absolute bottom-4 left-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#D4AF37]">
                      <i className={`${svc.icon} text-lg text-[#0F2A44]`} />
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-7">
                  <h3 className="text-xl font-bold text-[#0F2A44] mb-2">
                    {svc.title}
                  </h3>
                  <p className="text-gray-500 text-sm leading-relaxed mb-5">
                    {t(`service_${svc.key}_desc`)}
                  </p>

                  {/* Benefits */}
                  <ul className="space-y-2 mb-6">
                    {svc.benefits.map((b) => (
                      <li key={b} className="flex items-center gap-2.5">
                        <div className="w-4 h-4 flex items-center justify-center shrink-0">
                          <i className="ri-check-line text-xs text-[#D4AF37] font-bold" />
                        </div>
                        <span className="text-sm text-gray-600">{b}</span>
                      </li>
                    ))}
                  </ul>

                  <Link
                    to="/contact"
                    className="inline-flex items-center gap-2 bg-[#0F2A44] hover:bg-[#D4AF37] text-white hover:text-[#0F2A44] font-semibold px-6 py-3 rounded-full text-sm transition-all duration-200 whitespace-nowrap cursor-pointer"
                  >
                    Get a Free Quote
                    <i className="ri-arrow-right-line text-sm" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="bg-[#0F2A44] py-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Not sure which service you need?
          </h2>
          <p className="text-white/65 text-base mb-8">
            Contact us and we'll help you choose the perfect cleaning solution for your facility.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#B8952E] text-[#0F2A44] font-bold px-10 py-4 rounded-full text-base transition-all duration-200 hover:scale-105 whitespace-nowrap cursor-pointer"
          >
            {t("nav_cta")}
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default ServicesPage;
